// When the served design's header count is stored for the signed-in member, and what is never stored. Pure scheduling: the browser pieces (events, fetch,
// timers) are injected, so the rules are testable. See CartBadgePublisher.tsx for the wiring and cart-badge.ts for the count.
//
// The rules, each load-bearing:
//  · The design persists `sg-cart-v1` and then raises `sg:cartquotechange` on EVERY real cart change (add, remove, clear, quantity, restore, host merge).
//    That is the only trigger; a publish reads the record the design just wrote.
//  · Only a VERIFIED sign-in publishes (the server-proven cookie session). Signed out, or a legacy bearer session the server cannot verify: nothing is
//    sent and nothing is cleared (the member's stored snapshot belongs to the member, not to this browser).
//  · UNKNOWN is never zero. No readable record means nothing is sent. A zero is sent only from an explicit empty record, and never before the host
//    store has hydrated or while the host's merged-cart handover is still pending, because the design is still assembling the cart then.
//  · A trailing debounce collapses a burst (a restore followed by a host merge) into the final number, so a transient intermediate value is not stored.
//  · The same number for the same identity is not re-sent; a different identity starts clean.
//  · Writes for one identity are SERIALIZED: at most one is in flight, and a change that arrives meanwhile is re-read when it completes, so a slower older
//    request can never finish after a newer one and leave the older number stored. The LAST number the cart held is always the one left stored.

export const PUBLISH_DEBOUNCE_MS = 800;

export interface PublisherDeps {
  /** the raw `sg-cart-v1` value, or null */
  readRaw: () => string | null;
  /** the design's count for that raw value (cart-badge.ts `designCartBadge`) */
  countOf: (raw: string | null) => number | null;
  /** has the host cart store finished hydrating? */
  hydrated: () => boolean;
  /** is the host's merged-cart handover still waiting to be applied by the design? */
  handoverPending: () => boolean;
  /** send one count for the verified member; resolves true when stored */
  send: (count: number) => Promise<boolean>;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (t: unknown) => void;
}

export interface CartBadgePublisher {
  /** the verified identity key, or null when nobody verified is signed in */
  setIdentity: (who: string | null) => void;
  /** a cart change (the design's event), a sign-in, a focus: schedule a publish */
  poke: () => void;
  dispose: () => void;
}

export function createCartBadgePublisher(deps: PublisherDeps): CartBadgePublisher {
  let who: string | null = null;
  let timer: unknown = null;
  let sent: { who: string; count: number } | null = null;
  /** the one write in flight for the current identity; `dirty` = the cart changed while it was in the air */
  let flight: { who: string; dirty: boolean } | null = null;

  const cancel = () => {
    if (timer !== null) deps.clearTimer(timer);
    timer = null;
  };

  const attempt = () => {
    timer = null;
    const forWho = who;
    if (!forWho) return;
    if (flight?.who === forWho) {
      flight.dirty = true; // re-read the newest cart when the write in the air completes
      return;
    }
    const count = deps.countOf(deps.readRaw());
    if (count === null) return; // unknown stays unsent
    // the design is still assembling the cart: try again later rather than store a transient number (above all a zero)
    if (!deps.hydrated() || deps.handoverPending()) {
      timer = deps.setTimer(attempt, PUBLISH_DEBOUNCE_MS);
      return;
    }
    if (sent?.who === forWho && sent.count === count) return;
    const mine = { who: forWho, dirty: false };
    flight = mine;
    void deps.send(count).then((ok) => {
      if (flight === mine) flight = null;
      // an answer for a previous identity records nothing
      if (who !== forWho) return;
      if (ok && !mine.dirty) sent = { who: forWho, count };
      // the cart changed while this write was in the air: read it again now and store what it holds NOW (the write above, if it landed, is older)
      if (mine.dirty) {
        sent = null;
        cancel();
        timer = deps.setTimer(attempt, 0);
      }
    });
  };

  return {
    setIdentity(next) {
      if (next === who) return;
      who = next;
      sent = null;
      flight = null; // a write still in the air belongs to the previous identity and records nothing
      cancel();
      if (who) timer = deps.setTimer(attempt, PUBLISH_DEBOUNCE_MS);
    },
    poke() {
      if (!who) return;
      cancel();
      timer = deps.setTimer(attempt, PUBLISH_DEBOUNCE_MS);
    },
    dispose() {
      cancel();
      who = null;
      flight = null;
    },
  };
}
